import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import ServiceHighlights from './components/ServiceHighlights';
import StatsCounter from './components/StatsCounter';
import TestimonialsSlider from './components/TestimonialsSlider';
import CompanyStory from './components/CompanyStory';
import { useTheme } from '../../contexts/ThemeContext';

const HomeLanding = () => {
  const { isDarkMode } = useTheme();
  
  useEffect(() => {
    // Smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';
    
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Maayo Fabric Care - Premium Laundry Services in Coimbatore | Free Pickup & Delivery</title>
        <meta name="description" content="Experience premium laundry services in Coimbatore with Maayo Fabric Care. Professional wash, iron, dry clean & delivery services. Free pickup available. Book now!" />
        <meta name="keywords" content="laundry service Coimbatore, dry cleaning, wash and fold, pickup delivery, premium laundry care" />
        <meta property="og:title" content="Maayo Fabric Care - Premium Laundry Services in Coimbatore" />
        <meta property="og:description" content="Professional laundry services with free pickup & delivery. Trusted by 15,000+ customers across Coimbatore." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/home-landing" />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <Header />

        {/* Main Content */}
        <main className="relative z-0">
          {/* Hero Section */}
          <HeroSection />

          {/* Service Highlights */}
          <ServiceHighlights />

          {/* Stats Counter */}
          <StatsCounter />

          {/* Testimonials Slider */}
          <TestimonialsSlider />

          {/* Company Story */}
          <CompanyStory />
        </main>

        {/* Footer */}
        <footer className="relative z-0 bg-slate-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Maayo Fabric Care</h3>
                    <p className="text-sm font-inter font-extrabold tracking-[0.08em] text-gray-400">PREMIUM FABRIC CARE</p>
                  </div>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Your trusted partner for premium laundry and dry cleaning services in Coimbatore.
                  We provide professional garment care with convenient pickup and delivery.
                </p>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
                <ul className="space-y-2">
                  <li><a href="/home-landing" className="text-gray-400 hover:text-white transition-colors text-sm">Home</a></li>
                  <li><a href="/services-overview" className="text-gray-400 hover:text-white transition-colors text-sm">Services</a></li>
                  <li><a href="/collection-points" className="text-gray-400 hover:text-white transition-colors text-sm">Locations</a></li>
                  <li><a href="/contact-us" className="text-gray-400 hover:text-white transition-colors text-sm">Contact</a></li>
                </ul>
              </div>

              {/* Services */}
              <div>
                <h4 className="text-lg font-semibold mb-4">Services</h4>
                <ul className="space-y-2">
                  <li><span className="text-gray-400 text-sm">Wash & Fold</span></li>
                  <li><span className="text-gray-400 text-sm">Dry Cleaning</span></li>
                  <li><span className="text-gray-400 text-sm">Express Iron</span></li>
                  <li><span className="text-gray-400 text-sm">Free Delivery</span></li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    <div className="text-gray-400 text-sm">
                      <p className="font-semibold text-white leading-relaxed">Maayo Fabric Care</p>
                      <p className="leading-relaxed">30/1, Tank Street, Velappanaickenpudur, Chinnavedampatti, Coimbatore – 641049</p>
                      <p className="leading-relaxed mt-1">Location: Thudiyalur to Saravampatti Road, Sunnambu Kalavai bus stop</p>
                    </div>
                  </div>
                  <a href="tel:+919600044004" className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors">
                    <svg className="w-4 h-4 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                    </svg>
                    <span className="text-sm">96000 44004</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-gray-800 mt-12 pt-8">
              <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
                <p className="text-gray-400 text-sm">
                  © {new Date()?.getFullYear()} Maayo Fabric Care. All rights reserved.
                </p>
                <div className="flex space-x-6 mt-4 sm:mt-0">
                  <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">Privacy Policy</a>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">Terms of Service</a>
                </div>
              </div>
              <div className="text-center">
                <p className={`text-xs ${
                  isDarkMode ? 'text-slate-400' : 'text-gray-500'
                }`}>
                  Powered by: <a 
                    href="https://thamiratech.in/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`font-medium hover:underline transition-colors ${
                      isDarkMode ? 'text-yellow-400 hover:text-yellow-300' : 'text-primary hover:text-primary/80'
                    }`}
                  >
                    Thamira Technologies
                  </a>
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default HomeLanding;